/**
 * HeyKoala headless voice-call core — the LiveKit machinery of the embeddable
 * widget with NO DOM rendering. Powers both:
 *   • the full widget UI (src/embed/index.ts consumes a VoiceCall and renders it)
 *   • the headless SDK (window.HeyKoalaVoice.start — host site owns the DOM and
 *     reacts to the events this class emits)
 *
 * It requests a LiveKit token from the public widget endpoint, connects, plays
 * the agent's audio into a hidden <audio> container, and emits lifecycle events
 * ("status" / "transcript" / "error" / "end") plus the raw agent state.
 */
import {
  ConnectionState,
  ParticipantKind,
  RemoteAudioTrack,
  Room,
  RoomEvent,
  RpcError,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RpcInvocationData,
} from "livekit-client"

// ─── Types ──────────────────────────────────────────────────────────────────

/** Raw LiveKit agent state, from the `lk.agent.state` participant attribute. */
export type AgentState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking"
  | "initializing"
  | "disconnected"
  | "failed"

/**
 * Normalized, host-facing call status emitted on the "status" event. Derived
 * from the connection state + agent state — this is the vocabulary a host page
 * switches on to update its own DOM.
 */
export type CallStatus =
  | "connecting"
  | "reconnecting"
  | "connected"
  | "waiting_for_agent"
  | "listening"
  | "speaking"
  | "thinking"
  | "disconnected"
  | "ended"
  | "failed"

export interface TranscriptEvent {
  text: string
  /** true when this turn came from the visitor (mic or typed), false = agent. */
  fromSelf: boolean
  role: "user" | "agent"
}

export interface CallErrorEvent {
  code: string
  message: string
}

export interface VoiceCallOptions {
  publicKey: string
  /** API origin. Defaults to the origin of the loaded script's <src>. */
  apiBase: string
  /** Optional caller-supplied session id (else a random one is generated). */
  sessionId?: string
  /**
   * Enable the microphone automatically after connecting (default true). The
   * headless SDK relies on this; the widget UI also leaves it on.
   */
  autoMic?: boolean
  /**
   * Where to append the hidden <audio> elements for the agent's voice. When
   * omitted a hidden container is created on document.body. The widget passes
   * its own in-panel container so teardown is scoped to the dialog.
   */
  audioContainer?: HTMLElement
}

/** Payload shape per event name — used by on/off/emit for type-safety. */
export interface VoiceCallEventMap {
  status: CallStatus
  agentstate: AgentState
  transcript: TranscriptEvent
  error: CallErrorEvent
  connected: void
  end: void
}

type EventName = keyof VoiceCallEventMap
type Listener<E extends EventName> = (payload: VoiceCallEventMap[E]) => void

interface WidgetTokenResponse {
  token: string
  serverUrl: string
  roomName?: string
  participantName?: string
  participantIdentity?: string
  call_id?: string
  session_id?: string
  [key: string]: unknown
}

/**
 * Everything needed to join the LiveKit room, returned by {@link provisionCall}.
 * Lets an integrator bring their own LiveKit client (livekit-client /
 * @livekit/components-react) instead of using {@link VoiceCall}.
 */
export interface ProvisionResult {
  /** LiveKit access token (JWT). */
  token: string
  /** LiveKit server URL (wss://…). */
  serverUrl: string
  roomName?: string
  participantName?: string
  participantIdentity?: string
  /** CallLog id for this session, if the backend returned one. */
  callId: string | null
  /** Session id used for this provision (generated if not supplied). */
  sessionId: string
  /** The raw JSON body from the token endpoint, for advanced use. */
  raw: WidgetTokenResponse
}

export interface ProvisionOptions {
  publicKey: string
  apiBase: string
  sessionId?: string
}

function newSessionId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

// ─── API helpers ────────────────────────────────────────────────────────────

export function joinUrl(base: string, path: string): string {
  return base.replace(/\/+$/, "") + "/" + path.replace(/^\/+/, "")
}

export function bypassHeaders(apiBase: string): Record<string, string> {
  return /ngrok/i.test(apiBase) ? { "ngrok-skip-browser-warning": "true" } : {}
}

async function requestWidgetToken(
  apiBase: string,
  publicKey: string,
  sessionId: string
): Promise<WidgetTokenResponse> {
  const res = await fetch(
    joinUrl(apiBase, `api/v1/widgets/public/${publicKey}/token/`),
    {
      method: "POST",
      credentials: "omit",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...bypassHeaders(apiBase),
      },
      body: JSON.stringify({ session_id: sessionId }),
    }
  )
  if (!res.ok) throw new Error(`Widget token request failed (${res.status})`)
  return (await res.json()) as WidgetTokenResponse
}

/**
 * Provision a call WITHOUT connecting: request a LiveKit token from the public
 * widget endpoint and return everything needed to join. Use this when you want
 * to drive the room yourself with the LiveKit client SDK
 * (livekit-client / @livekit/components-react) instead of {@link VoiceCall}:
 *
 *   const { token, serverUrl } = await provisionCall({ publicKey, apiBase })
 *   const room = new Room()
 *   await room.connect(serverUrl, token)   // ...then render with LiveKit UI
 */
export async function provisionCall(
  opts: ProvisionOptions
): Promise<ProvisionResult> {
  const sessionId = opts.sessionId ?? newSessionId()
  const raw = await requestWidgetToken(opts.apiBase, opts.publicKey, sessionId)
  return {
    token: raw.token,
    serverUrl: raw.serverUrl,
    roomName: raw.roomName,
    participantName: raw.participantName,
    participantIdentity: raw.participantIdentity,
    callId: raw.call_id ?? null,
    sessionId: raw.session_id ?? sessionId,
    raw,
  }
}

// ─── Status derivation ──────────────────────────────────────────────────────

/**
 * Collapse (agent state, connection state) into the single normalized status
 * emitted to hosts. Mirrors the widget's stageClass/statusLabel semantics.
 */
export function deriveStatus(
  agent: AgentState,
  conn: ConnectionState,
  hasAgent: boolean,
  hadConnection: boolean
): CallStatus {
  if (conn === ConnectionState.Connecting) return "connecting"
  if (conn === ConnectionState.Reconnecting) return "reconnecting"
  if (conn === ConnectionState.Disconnected)
    return hadConnection ? "ended" : "disconnected"
  if (!hasAgent) return "waiting_for_agent"
  if (agent === "speaking") return "speaking"
  if (agent === "listening") return "listening"
  if (agent === "thinking" || agent === "initializing") return "thinking"
  if (agent === "failed" || agent === "disconnected") return "ended"
  return "connected"
}

// ─── Agent → host-page navigation ───────────────────────────────────────────

interface NavResult {
  ok: boolean
  reason?: string
}

/**
 * Navigate the host page to `path`, enforcing same-origin and giving the host
 * a chance to veto via a cancelable `heykoala-widget-navigate` event. Returns
 * a structured result so the "navigate" RPC handler can report success/failure
 * back to the agent.
 */
export function navigateTo(path: string, replace: boolean): NavResult {
  if (typeof path !== "string" || !path.length) {
    return { ok: false, reason: "empty path" }
  }

  let href: string
  try {
    if (path.startsWith("/")) {
      href = new URL(path, window.location.origin).href
    } else {
      const target = new URL(path, window.location.href)
      if (target.origin !== window.location.origin) {
        console.warn("[HeyKoala widget] cross-origin nav blocked:", target.href)
        return { ok: false, reason: "cross-origin navigation is not allowed" }
      }
      href = target.href
    }
  } catch (e) {
    console.warn("[HeyKoala widget] invalid nav path:", path, e)
    return { ok: false, reason: "invalid path" }
  }

  const ev = new CustomEvent("heykoala-widget-navigate", {
    detail: { path, replace },
    cancelable: true,
  })
  const proceed = window.dispatchEvent(ev)
  if (!proceed) return { ok: true } // host handled it

  if (replace) window.location.replace(href)
  else window.location.assign(href)
  return { ok: true }
}

// ─── VoiceCall ──────────────────────────────────────────────────────────────

/**
 * One live voice call. Create, wire listeners with `on`, then `start()`.
 * Emits "status"/"agentstate"/"transcript"/"error"/"connected"/"end".
 */
export class VoiceCall {
  private readonly publicKey: string
  private readonly apiBase: string
  private readonly autoMic: boolean
  private readonly explicitSessionId?: string
  private readonly ownsAudioContainer: boolean
  private audioContainer: HTMLElement

  private _room: Room | null = null
  private listeners = new Map<EventName, Set<Listener<EventName>>>()
  private seenSegments = new Set<string>()

  /** Provisioning result once the call has started (token, serverUrl, …). */
  provision: ProvisionResult | null = null

  // ── observable state ──
  agentState: AgentState = "idle"
  connState: ConnectionState = ConnectionState.Disconnected
  hasAgent = false
  hadConnection = false
  muted = false
  callId: string | null = null
  sessionId: string | null = null
  private started = false
  private disposed = false

  constructor(opts: VoiceCallOptions) {
    this.publicKey = opts.publicKey
    this.apiBase = opts.apiBase
    this.autoMic = opts.autoMic !== false
    this.explicitSessionId = opts.sessionId
    if (opts.audioContainer) {
      this.audioContainer = opts.audioContainer
      this.ownsAudioContainer = false
    } else {
      const c = document.createElement("div")
      c.setAttribute("data-heykoala-audio", this.publicKey)
      c.style.display = "none"
      c.setAttribute("aria-hidden", "true")
      document.body.appendChild(c)
      this.audioContainer = c
      this.ownsAudioContainer = true
    }
  }

  // ── event emitter ──
  on<E extends EventName>(event: E, cb: Listener<E>): this {
    let set = this.listeners.get(event)
    if (!set) {
      set = new Set()
      this.listeners.set(event, set)
    }
    set.add(cb as Listener<EventName>)
    return this
  }

  off<E extends EventName>(event: E, cb: Listener<E>): this {
    this.listeners.get(event)?.delete(cb as Listener<EventName>)
    return this
  }

  private emit<E extends EventName>(
    event: E,
    payload: VoiceCallEventMap[E]
  ): void {
    const set = this.listeners.get(event)
    if (!set) return
    for (const cb of set) {
      try {
        ;(cb as Listener<E>)(payload)
      } catch (e) {
        console.error(`[HeyKoala] listener for "${event}" threw`, e)
      }
    }
  }

  /** Current normalized status. */
  get status(): CallStatus {
    return deriveStatus(
      this.agentState,
      this.connState,
      this.hasAgent,
      this.hadConnection
    )
  }

  /**
   * The live LiveKit Room (null until start() connects, or after end()).
   * Exposed so integrators can render their own UI with the LiveKit client SDK
   * — e.g. `<RoomContext.Provider value={call.room}>` with
   * @livekit/components-react, or livekit-client APIs directly against it.
   */
  get room(): Room | null {
    return this._room
  }

  private lastStatus: CallStatus | null = null
  /** Emit "status" only when the derived value actually changes. */
  private emitStatus(): void {
    const s = this.status
    if (s === this.lastStatus) return
    this.lastStatus = s
    this.emit("status", s)
  }

  // ── lifecycle ──
  async start(): Promise<void> {
    if (this.started || this.disposed) return
    this.started = true

    this.sessionId = this.explicitSessionId ?? newSessionId()

    // Enter the connecting state up-front so hosts get an immediate signal.
    this.connState = ConnectionState.Connecting
    this.emitStatus()

    let provision: ProvisionResult
    try {
      provision = await provisionCall({
        publicKey: this.publicKey,
        apiBase: this.apiBase,
        sessionId: this.sessionId,
      })
    } catch (e) {
      if (this.disposed) return
      this.fail("token_failed", e, "Could not start call. Try again.")
      return
    }
    if (this.disposed) return
    this.provision = provision
    this.callId = provision.callId
    this.sessionId = provision.sessionId

    const room = new Room()
    this._room = room
    this.setupRoom(room)

    try {
      await room.connect(provision.serverUrl, provision.token)
      if (this.disposed) return
      if (this.autoMic) await room.localParticipant.setMicrophoneEnabled(true)
    } catch (e) {
      if (this.disposed) return
      this.fail("connect_failed", e, "Could not start call. Try again.")
    }
  }

  private fail(code: string, e: unknown, fallback: string): void {
    const message = e instanceof Error && e.message ? e.message : fallback
    console.error(`[HeyKoala] ${code}`, e)
    this.emit("error", { code, message })
    void this.end()
  }

  /** Mute/unmute the visitor's microphone. */
  async setMuted(muted: boolean): Promise<void> {
    if (!this._room) return
    try {
      await this._room.localParticipant.setMicrophoneEnabled(!muted)
      this.muted = muted
      this.emitStatus()
    } catch (e) {
      console.error("[HeyKoala] mute toggle failed", e)
    }
  }

  mute(): Promise<void> {
    return this.setMuted(true)
  }

  unmute(): Promise<void> {
    return this.setMuted(false)
  }

  /**
   * Send an exact typed answer as a visitor turn (topic "lk.chat"). The agent
   * treats it like a spoken turn but with exact spelling (no STT errors).
   */
  sendText(text: string): void {
    const value = (text || "").trim()
    if (!this._room || !value) return
    this._room.localParticipant
      .sendText(value, { topic: "lk.chat" })
      .catch(e => console.warn("[HeyKoala] sendText failed", e))
  }

  /** End the call and release resources. Emits "end" once. */
  async end(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    if (this._room) {
      await this._room.disconnect().catch(() => {})
      this._room = null
    }
    if (this.ownsAudioContainer) this.audioContainer.remove()
    this.emit("end", undefined)
  }

  // ── livekit-client wiring ──
  private setupRoom(r: Room): void {
    // Browser-control RPC: the agent calls "navigate" on us. Pre-registered
    // before connect so it's live the moment the agent joins.
    r.registerRpcMethod("navigate", async (data: RpcInvocationData) => {
      let path = ""
      let replace = false
      try {
        const msg = JSON.parse(data.payload) as {
          path?: string
          replace?: boolean
        }
        path = msg.path ?? ""
        replace = !!msg.replace
      } catch {
        throw new RpcError(1, "invalid payload")
      }
      if (!path) throw new RpcError(1, "missing path")
      const res = navigateTo(path, replace)
      if (!res.ok) throw new RpcError(2, res.reason || "navigation blocked")
      return JSON.stringify({ ok: true, path })
    })

    // Live transcript via text stream (skip interim, dedupe by segment id).
    r.registerTextStreamHandler(
      "lk.transcription",
      async (reader, participantInfo) => {
        try {
          const attrs = reader.info.attributes ?? {}
          if (attrs["lk.transcription_final"] === "false") return
          const segId = attrs["lk.segment_id"]
          if (segId) {
            if (this.seenSegments.has(segId)) return
            this.seenSegments.add(segId)
          }
          const text = (await reader.readAll()).trim()
          if (!text) return
          this.emitTranscript(
            text,
            participantInfo.identity === r.localParticipant.identity
          )
        } catch (e) {
          console.warn("[HeyKoala] transcript handler error", e)
        }
      }
    )

    // Legacy delivery: some agent SDKs publish via TranscriptionReceived.
    r.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
      const fromSelf =
        !!participant && participant.identity === r.localParticipant.identity
      for (const seg of segments) {
        if (!seg.final) continue
        if (seg.id) {
          if (this.seenSegments.has(seg.id)) continue
          this.seenSegments.add(seg.id)
        }
        const text = (seg.text || "").trim()
        if (!text) continue
        this.emitTranscript(text, fromSelf)
      }
    })

    r.on(RoomEvent.ConnectionStateChanged, (s: ConnectionState) => {
      this.connState = s
      if (s === ConnectionState.Connected) {
        if (!this.hadConnection) this.emit("connected", undefined)
        this.hadConnection = true
      }
      this.emitStatus()
    })

    r.on(
      RoomEvent.TrackSubscribed,
      (
        track: RemoteTrack,
        _pub: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        if (track.kind === "audio") {
          const audioEl = (track as RemoteAudioTrack).attach()
          this.audioContainer.appendChild(audioEl)
          if (participant.kind === ParticipantKind.AGENT) {
            this.hasAgent = true
            const attrState = participant.attributes?.["lk.agent.state"]
            if (attrState) this.agentState = attrState as AgentState
            this.emitAgentState()
          }
        }
      }
    )

    r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
      track.detach().forEach(e => e.remove())
    })

    r.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
      if (p.kind === ParticipantKind.AGENT) {
        const attrState = p.attributes?.["lk.agent.state"]
        if (attrState) this.agentState = attrState as AgentState
        this.emitAgentState()
      }
    })

    r.on(
      RoomEvent.ParticipantAttributesChanged,
      (changed: Record<string, string>, p) => {
        if (
          (p as RemoteParticipant).kind === ParticipantKind.AGENT &&
          "lk.agent.state" in changed
        ) {
          this.agentState = changed["lk.agent.state"] as AgentState
          this.emitAgentState()
        }
      }
    )

    r.on(RoomEvent.Disconnected, () => {
      this.connState = ConnectionState.Disconnected
      this.emitStatus()
    })
  }

  private emitAgentState(): void {
    this.emit("agentstate", this.agentState)
    this.emitStatus()
  }

  private emitTranscript(text: string, fromSelf: boolean): void {
    this.emit("transcript", {
      text,
      fromSelf,
      role: fromSelf ? "user" : "agent",
    })
  }
}
